import { useState, useEffect } from "react";
import { useMouseEventLock } from "./event_hooks";

type DragCoordinates = { x: number; y: number };
//type DragSelection = DragCoordinates & { width: number; height: number };

function useBlobDragSelect(
  blobContainerRef: React.RefObject<HTMLDivElement>,
  blobTokenRefs: React.RefObject<HTMLElement[]>
): {
  highlightedTokenIndeces: number[];
  isSelectionPopupOpen: boolean;
  clearSelectionClosePopup: () => void;
} {
  const { arePaused } = useMouseEventLock();
  //const arePaused = false;
  const [dragStart, setDragStart] = useState<DragCoordinates | null>(null);
  const [highlightedTokenIndeces, setHighlightedTokenIndeces] = useState<
    number[]
  >([]);
  const [isSelectionPopupOpen, setIsSelectionPopupOpen] = useState(false);

  const handleMouseDown = (e: MouseEvent) => {
    if (arePaused || !blobContainerRef.current) {
      return;
    }
    const rect = blobContainerRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    setDragStart({ x: offsetX, y: offsetY });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (
      arePaused ||
      dragStart === null ||
      !blobContainerRef.current ||
      !blobTokenRefs.current
    ) {
      return;
    }
    const rect = blobContainerRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    const newSelection = {
      x: Math.min(dragStart.x, offsetX),
      y: Math.min(dragStart.y, offsetY),
      width: Math.abs(offsetX - dragStart.x),
      height: Math.abs(offsetY - dragStart.y),
    };

    let firstIntersectedIndex = -1;
    let lastIntersectedIndex = -1;

    blobTokenRefs.current.forEach((tokenEl, index) => {
      if (isElementInSelection(tokenEl, newSelection, blobContainerRef)) {
        if (firstIntersectedIndex === -1) {
          firstIntersectedIndex = index;
        }
        lastIntersectedIndex = index;
      }
    });

    if (firstIntersectedIndex !== -1 && lastIntersectedIndex !== -1) {
      const selectedIndices: number[] = Array.from(
        { length: lastIntersectedIndex - firstIntersectedIndex + 1 },
        (_, i) => i + firstIntersectedIndex
      );
      setHighlightedTokenIndeces(selectedIndices);
    } else {
      setHighlightedTokenIndeces([]);
    }
  };

  const handleMouseUp = () => {
    setDragStart(null);
    if (highlightedTokenIndeces.length === 1) {
      setHighlightedTokenIndeces([]);
    } else if (highlightedTokenIndeces.length > 1) {
      setIsSelectionPopupOpen(true);
    }
  };

  const handleOtherMouseUp = (e: MouseEvent) => {
    if (dragStart === null) {
      return;
    }
    if (
      blobContainerRef.current &&
      !blobContainerRef.current.contains(e.target as Node)
    ) {
      setDragStart(null);
      setHighlightedTokenIndeces([]);
    }
  };

  useEffect(() => {
    if (blobContainerRef.current) {
      blobContainerRef.current.addEventListener("mousedown", handleMouseDown);
      blobContainerRef.current.addEventListener("mousemove", handleMouseMove);
      blobContainerRef.current.addEventListener("mouseup", handleMouseUp);
    }

    document.addEventListener("mouseup", handleOtherMouseUp);

    return () => {
      if (blobContainerRef.current) {
        blobContainerRef.current.removeEventListener(
          "mousedown",
          handleMouseDown
        );
        blobContainerRef.current.removeEventListener(
          "mousemove",
          handleMouseMove
        );
        blobContainerRef.current.removeEventListener("mouseup", handleMouseUp);
      }
      document.removeEventListener("mouseup", handleOtherMouseUp);
    };
  }, [
    blobContainerRef.current,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  ]);

  return {
    highlightedTokenIndeces,
    isSelectionPopupOpen,
    clearSelectionClosePopup: () => {
      setHighlightedTokenIndeces([]);
      setIsSelectionPopupOpen(false);
    },
  };
}

export default useBlobDragSelect;

const isElementInSelection = (
  element: HTMLElement,
  selection: { x: number; y: number; width: number; height: number },
  containerRef: React.RefObject<HTMLDivElement>
): boolean => {
  if (!element || !containerRef.current) {
    return false;
  }
  const containerRect = containerRef.current.getBoundingClientRect();
  const rect = element.getBoundingClientRect();

  const relativeRect = {
    left: rect.left - containerRect.left,
    top: rect.top - containerRect.top,
    width: rect.width,
    height: rect.height,
  };

  // Check for intersection
  const isIntersecting =
    selection.x < relativeRect.left + relativeRect.width &&
    selection.x + selection.width > relativeRect.left &&
    selection.y < relativeRect.top + relativeRect.height &&
    selection.y + selection.height > relativeRect.top;

  return isIntersecting;
};
